import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import landlordService from '../services/landlordService';
import propertyService from '../services/propertyService';
import ApplyLinkCard from '../components/property/ApplyLinkCard';
import FormField from '../components/common/FormField';
import Alert from '../components/common/Alert';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { statusLabel, OUTCOME_LABELS } from '../utils/applicationStatus';
import { formatCurrency, formatDate } from '../utils/formatters';
import './ApplicantPages.css';

const messageFrom = (err, fallback) => err.response?.data?.message || err.message || fallback;

const asList = (payload) => (Array.isArray(payload) ? payload : payload?.data || []);

const COLUMNS = [
  { key: 'applicant_name', label: 'Applicant' },
  { key: 'monthly_income', label: 'Income' },
  { key: 'rent_to_income', label: 'Rent / income' },
  { key: 'verified', label: 'Checks verified' },
  { key: 'documents', label: 'Documents' },
  { key: 'references', label: 'References' }
];

const sortValue = (row, key) => {
  if (key === 'verified') return row.checks.verified;
  if (key === 'references') return row.references.completed;
  return row[key] ?? -1;
};

// Compare the people who applied for a property. The application link itself is managed on the property page,
// and shown here too so it is one click away.
const PropertyApplicantsPage = () => {
  const [params] = useSearchParams();
  const [organisations, setOrganisations] = useState(null);
  const [orgId, setOrgId] = useState(params.get('org') || '');
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState(params.get('property') || '');
  const [comparison, setComparison] = useState(null);
  const [notice, setNotice] = useState(null);
  const [sort, setSort] = useState({ key: 'rent_to_income', dir: 'asc' });

  useEffect(() => {
    landlordService
      .organisations()
      .then((data) => {
        const list = asList(data);
        setOrganisations(list);
        setOrgId((current) => current || (list.length === 1 ? String(list[0].id) : ''));
      })
      .catch((err) => {
        setOrganisations([]);
        setNotice({ type: 'error', message: messageFrom(err, 'Your organisations could not be loaded.') });
      });
  }, []);

  useEffect(() => {
    if (!orgId) {
      setProperties([]);
      return;
    }
    propertyService
      .list(orgId)
      .then((data) => {
        const list = asList(data);
        setProperties(list);
        // Keep a property chosen from the property page; otherwise pick the only one, or ask.
        setPropertyId((current) =>
          list.some((p) => String(p.id) === current) ? current : list.length === 1 ? String(list[0].id) : ''
        );
      })
      .catch((err) => setNotice({ type: 'error', message: messageFrom(err, 'Properties could not be loaded.') }));
  }, [orgId]);

  useEffect(() => {
    setComparison(null);
    if (!propertyId) return;
    landlordService
      .comparison(propertyId)
      .then(setComparison)
      .catch((err) => setNotice({ type: 'error', message: messageFrom(err, 'This property could not be loaded.') }));
  }, [propertyId]);

  const rows = useMemo(() => {
    const list = [...(comparison?.applicants || [])];
    list.sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [comparison, sort]);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const ariaSort = (key) => (sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none');

  const orgOptions = (organisations || []).map((o) => ({ value: String(o.id), label: o.name }));
  const propertyOptions = properties.map((p) => ({ value: String(p.id), label: p.address || `Property ${p.id}` }));

  if (!organisations) return <LoadingSpinner />;

  if (organisations.length === 0) {
    return (
      <div className="ap-page">
        <div className="ap-card">
          <h2>Set up your organisation first</h2>
          <Link to="/get-started" className="btn-primary ap-button">Get started</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="ap-page ap-tools">
      <h1>Applicants</h1>
      <p className="ap-muted">
        Compare the people who have applied for a property. Flags point out things to look at. They never reject anyone:
        you make the decision.
      </p>

      {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}

      <div className="ap-selectors">
        <FormField label="Organisation" name="organisation" type="select" options={orgOptions} value={orgId} onChange={(e) => setOrgId(e.target.value)} />
        <FormField label="Property" name="property" type="select" options={propertyOptions} value={propertyId} onChange={(e) => setPropertyId(e.target.value)} disabled={!orgId} />
      </div>

      {orgId && properties.length === 0 && (
        <div className="ap-card">
          <p>You have no properties yet.</p>
          <Link to="/properties/new" className="btn-primary ap-button">Add a property</Link>
        </div>
      )}

      {propertyId && <ApplyLinkCard key={propertyId} propertyId={propertyId} />}

      {propertyId && comparison && (
        <section className="ap-card" aria-labelledby="compare-title">
          <h2 id="compare-title">Compare applicants</h2>
          <p className="ap-muted">
            Rent {formatCurrency(comparison.property.monthly_rent)} a month. Rent above{' '}
            {Math.round(comparison.flag_threshold * 100)}% of gross income is flagged.
          </p>
          {rows.length === 0 ? (
            <p>No one has submitted an application for this property yet.</p>
          ) : (
            <div className="ap-table-wrap">
              <table className="ap-table">
                <thead>
                  <tr>
                    {COLUMNS.map((c) => (
                      <th key={c.key} scope="col" aria-sort={ariaSort(c.key)}>
                        <button type="button" onClick={() => toggleSort(c.key)}>
                          {c.label}{sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                        </button>
                      </th>
                    ))}
                    <th scope="col">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.application_id}>
                      <td>
                        <strong>{r.applicant_name}</strong>
                        <div><StatusBadge status={r.status} text={statusLabel(r.status)} /></div>
                        {r.decision && <div className="ap-muted">Decision: {OUTCOME_LABELS[r.decision] || r.decision}</div>}
                        {r.submitted_at && <div className="ap-muted">Submitted {formatDate(r.submitted_at)}</div>}
                      </td>
                      <td>
                        {r.monthly_income ? formatCurrency(r.monthly_income) : '—'}
                        <div className="ap-muted">{r.income_verified ? 'Verified' : 'Declared'}</div>
                      </td>
                      <td>
                        {r.rent_to_income === null ? (
                          '—'
                        ) : (
                          <span className={r.rent_to_income > comparison.flag_threshold ? 'ap-ratio--high' : 'ap-ratio--ok'}>
                            {Math.round(r.rent_to_income * 100)}%
                          </span>
                        )}
                      </td>
                      <td>{r.checks.verified} of {r.checks.total}</td>
                      <td>{r.documents}</td>
                      <td>{r.references.completed} of {r.references.total}</td>
                      <td>
                        {r.flags.length === 0 ? (
                          <span className="ap-muted">None</span>
                        ) : (
                          <ul className="ap-flags">{r.flags.map((f) => <li key={f}>{f}</li>)}</ul>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default PropertyApplicantsPage;
